// Caching policy for the course-data TanStack Query reads (task 4.4c),
// extracted out of CanvasDataContext.tsx at task 4.5 purely to keep that
// file under its line budget -- these values are not context state, just
// shared constants. TanStack Query's default staleTime is 0 -- every read
// is stale the instant it resolves and refetches on the next observer
// mount or fetchQuery call -- and its default gcTime is 5 minutes (how long
// an *unobserved* cache entry survives before eviction). Setting a real
// staleTime without also raising gcTime to match is a trap: an entry whose
// staleTime exceeds gcTime gets garbage-collected before it ever goes
// stale, silently capping the effective cache window at 5 minutes no
// matter what staleTime says. Every constant pair below states both, and
// gcTime is always >= staleTime.
//
// Recovered ground truth (`git show d7311881^:UI/src/services/enhancedApiService.ts`,
// enhancedApiService.ts deleted in task 4.4b):
//   courses:     { ttl: 15 * 60 * 1000 }  // 15 minutes
//   grades:      { ttl:  5 * 60 * 1000 }  //  5 minutes
//   assignments: { ttl: 10 * 60 * 1000 }  // 10 minutes
// That config had 3 categories; the read paths in CanvasDataContext.tsx
// have 3 families (courses, courseDetails, courseHistory -- assignmentNotes
// lives in useAssignmentNotes.js). The mapping below is therefore a
// judgement call against each resource's real-world volatility, not a
// lookup -- see the comment on each pair.
//
// Task 4.5 guardrail: do not change these values, and do not change what
// identity data (mode + userId, and courseId for the per-course families)
// the query keys built in CanvasDataContext.tsx encode.

// courses: maps directly onto the old `courses` entry -- same resource.
export const COURSES_STALE_TIME_MS = 15 * 60 * 1000; // 15 min -- mirrors old enhancedApiService `courses` TTL
export const COURSES_GC_TIME_MS = 20 * 60 * 1000; // 20 min -- margin above staleTime so the entry survives long enough to go stale

// courseDetails: this screen surfaces per-assignment grades, which change
// as instructors post or update them -- closer in volatility to the old
// `grades` TTL than to `courses` or `assignments`, since grades are what a
// parent is most likely to be checking freshness on here.
export const COURSE_DETAILS_STALE_TIME_MS = 5 * 60 * 1000; // 5 min -- mirrors old enhancedApiService `grades` TTL
export const COURSE_DETAILS_GC_TIME_MS = 10 * 60 * 1000; // 10 min -- margin above staleTime

// courseHistory: the old config has no `history` entry at all -- this
// family didn't exist under enhancedApiService, so there is no TTL to
// recover for it. Grade history is functionally a view over the same
// underlying grade data as courseDetails, so it gets the same volatility
// treatment. Per the guardrail (prefer short when genuinely unclear, since
// stale grades presented as current are worse than an extra request), it
// is NOT bumped up to the longer `courses`/`assignments` TTLs just because
// "historical" sounds static -- a course whose term hasn't ended yet can
// still have its history change.
export const COURSE_HISTORY_STALE_TIME_MS = 5 * 60 * 1000; // 5 min -- no old TTL existed for this family; erred short, same bucket as courseDetails
export const COURSE_HISTORY_GC_TIME_MS = 10 * 60 * 1000; // 10 min -- margin above staleTime
